<script lang="ts">
  /** Style controls for the current selection. Shown only for text and shape items. */
  import type { BoardItemKind, BoardItemStyle, ShapeVariant } from '../../lib/types';

  interface Props {
    kind: BoardItemKind;
    style: BoardItemStyle;
    onChange: (patch: BoardItemStyle) => void;
    onDelete: () => void;
  }

  let { kind, style, onChange, onDelete }: Props = $props();

  const SWATCHES = [
    { fill: '#dbeafe', stroke: '#93c5fd' },
    { fill: '#dcfce7', stroke: '#86efac' },
    { fill: '#fef3c7', stroke: '#fcd34d' },
    { fill: '#fee2e2', stroke: '#fca5a5' },
    { fill: '#f3e8ff', stroke: '#d8b4fe' },
    { fill: '#fce7f3', stroke: '#f9a8d4' },
    { fill: '#cffafe', stroke: '#67e8f9' },
    { fill: '#ffedd5', stroke: '#fdba74' },
    { fill: '#e5e7eb', stroke: '#9ca3af' },
    { fill: '#ffffff', stroke: '#d1d5db' },
  ];
  const TEXT_COLORS = [
    '#1f2937',
    '#2563eb',
    '#059669',
    '#d97706',
    '#dc2626',
    '#7c3aed',
    '#db2777',
    '#0891b2',
    '#4b5563',
    '#000000',
  ];
  const QUICK_FONT_SIZES = ['12', '16', '24', '36'];
  const FONT_SIZES = [
    '8',
    '10',
    '12',
    '14',
    '16',
    '18',
    '20',
    '24',
    '28',
    '32',
    '36',
    '48',
    '64',
    '72',
    '96',
  ];
  const SHAPE_VARIANTS: ShapeVariant[] = ['rectangle', 'ellipse', 'diamond'];

  const shapeLabels: Record<ShapeVariant, string> = {
    rectangle: '▭',
    ellipse: '◯',
    diamond: '◇',
  };

  function pickCustomColor(event: Event, property: 'fill' | 'stroke' | 'textColor') {
    onChange({ [property]: (event.currentTarget as HTMLInputElement).value });
  }
</script>

<div class="style-panel">
  {#if kind === 'shape'}
    <div class="style-group">
      <span class="style-label">Shape</span>
      <div class="style-row">
        {#each SHAPE_VARIANTS as variant}
          <button
            class="chip"
            class:active={(style.shapeVariant ?? 'rectangle') === variant}
            type="button"
            title={variant}
            aria-label={variant}
            onclick={() => onChange({ shapeVariant: variant })}
          >
            {shapeLabels[variant]}
          </button>
        {/each}
      </div>
    </div>

    <div class="style-group">
      <span class="style-label">Color</span>
      <div class="style-row">
        {#each SWATCHES as swatch}
          <button
            class="swatch"
            class:active={style.fill === swatch.fill}
            type="button"
            aria-label="Fill {swatch.fill}"
            style="background: {swatch.fill}; border-color: {swatch.stroke};"
            onclick={() => onChange({ fill: swatch.fill, stroke: swatch.stroke })}
          ></button>
        {/each}
        <label class="color-picker" title="Custom fill color">
          <span class="sr-only">Custom fill color</span>
          <input
            type="color"
            aria-label="Custom fill color"
            value={style.fill ?? '#dbeafe'}
            onchange={(event) => pickCustomColor(event, 'fill')}
          />
        </label>
      </div>
    </div>
  {/if}

  {#if kind === 'text'}
    <div class="style-group">
      <span class="style-label">Size</span>
      <div class="style-row">
        {#each QUICK_FONT_SIZES as size}
          <button
            class="chip"
            class:active={(style.fontSize ?? '16') === size}
            type="button"
            onclick={() => onChange({ fontSize: size })}
          >
            {size}
          </button>
        {/each}
        <select
          class="font-size-select"
          class:active={!QUICK_FONT_SIZES.includes(style.fontSize ?? '16')}
          aria-label="More font sizes"
          value={style.fontSize ?? '16'}
          onchange={(event) =>
            onChange({ fontSize: (event.currentTarget as HTMLSelectElement).value })}
        >
          {#each FONT_SIZES as size}
            <option value={size}>{size}px</option>
          {/each}
        </select>
      </div>
    </div>

    <div class="style-group">
      <span class="style-label">Color</span>
      <div class="style-row">
        {#each TEXT_COLORS as color}
          <button
            class="swatch"
            class:active={(style.textColor ?? '#1f2937') === color}
            type="button"
            aria-label="Text color {color}"
            style="background: {color}; border-color: {color};"
            onclick={() => onChange({ textColor: color })}
          ></button>
        {/each}
        <label class="color-picker" title="Custom text color">
          <span class="sr-only">Custom text color</span>
          <input
            type="color"
            aria-label="Custom text color"
            value={style.textColor ?? '#1f2937'}
            onchange={(event) => pickCustomColor(event, 'textColor')}
          />
        </label>
      </div>
    </div>

    <div class="style-group">
      <span class="style-label">Background</span>
      <div class="style-row">
        {#each SWATCHES as swatch}
          <button
            class="swatch"
            class:active={style.fill === swatch.fill}
            type="button"
            aria-label="Text background {swatch.fill}"
            style="background: {swatch.fill}; border-color: {swatch.stroke};"
            onclick={() => onChange({ fill: swatch.fill })}
          ></button>
        {/each}
        <label class="color-picker" title="Custom text background color">
          <span class="sr-only">Custom text background color</span>
          <input
            type="color"
            aria-label="Custom text background color"
            value={style.fill ?? '#ffffff'}
            onchange={(event) => pickCustomColor(event, 'fill')}
          />
        </label>
      </div>
    </div>

    <div class="style-group">
      <span class="style-label">Border</span>
      <div class="style-row">
        {#each SWATCHES as swatch}
          <button
            class="swatch"
            class:active={style.stroke === swatch.stroke}
            type="button"
            aria-label="Text border {swatch.stroke}"
            style="background: {swatch.stroke}; border-color: {swatch.stroke};"
            onclick={() => onChange({ stroke: swatch.stroke })}
          ></button>
        {/each}
        <label class="color-picker" title="Custom text border color">
          <span class="sr-only">Custom text border color</span>
          <input
            type="color"
            aria-label="Custom text border color"
            value={style.stroke ?? '#d1d5db'}
            onchange={(event) => pickCustomColor(event, 'stroke')}
          />
        </label>
      </div>
    </div>

    <div class="style-group">
      <span class="style-label">Style</span>
      <div class="style-row">
        <button
          class="chip bold"
          class:active={style.fontWeight === '700'}
          type="button"
          aria-label="Bold"
          onclick={() => onChange({ fontWeight: style.fontWeight === '700' ? '500' : '700' })}
          >B</button
        >
        <button
          class="chip italic"
          class:active={style.fontStyle === 'italic'}
          type="button"
          aria-label="Italic"
          onclick={() =>
            onChange({ fontStyle: style.fontStyle === 'italic' ? 'normal' : 'italic' })}>I</button
        >
      </div>
    </div>

    <div class="style-group">
      <span class="style-label">Horizontal alignment</span>
      <div class="style-row">
        {#each ['left', 'center', 'right'] as alignment}
          <button
            class="chip align"
            class:active={(style.textAlign ?? 'left') === alignment}
            type="button"
            aria-label="Align {alignment}"
            onclick={() => onChange({ textAlign: alignment })}>{alignment[0].toUpperCase()}</button
          >
        {/each}
      </div>
    </div>

    <div class="style-group">
      <span class="style-label">Vertical alignment</span>
      <div class="style-row">
        {#each ['flex-start', 'center', 'flex-end'] as alignment, index}
          <button
            class="chip align"
            class:active={(style.verticalAlign ?? 'center') === alignment}
            type="button"
            aria-label="Align {['top', 'middle', 'bottom'][index]}"
            onclick={() => onChange({ verticalAlign: alignment })}>{['T', 'M', 'B'][index]}</button
          >
        {/each}
      </div>
    </div>
  {/if}

  <div class="style-group">
    <button class="delete-btn" type="button" onclick={onDelete}>Remove from board</button>
  </div>
</div>

<style>
  .style-panel {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.625rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    width: 14rem;
  }
  .style-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .style-label {
    font-size: 0.5625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #9ca3af;
  }
  .style-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
  }
  .chip,
  .font-size-select {
    padding: 0.1875rem 0.375rem;
    font-size: 0.6875rem;
    font-family: inherit;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    background: white;
    color: #374151;
    cursor: pointer;
    line-height: 1.3;
  }
  .chip.bold {
    font-weight: 700;
  }
  .chip.italic {
    font-style: italic;
    font-weight: 700;
  }
  .chip.align {
    min-width: 1.5rem;
  }
  .chip:hover,
  .font-size-select:hover {
    background: #f9fafb;
  }
  .chip.active,
  .font-size-select.active {
    border-color: #2563eb;
    color: #2563eb;
    background: #eff6ff;
  }
  .swatch,
  .color-picker {
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 0.25rem;
    cursor: pointer;
    padding: 0;
  }
  .swatch {
    border: 2px solid;
  }
  .swatch.active {
    box-shadow:
      0 0 0 2px white,
      0 0 0 3px #2563eb;
  }
  .color-picker {
    overflow: hidden;
    border: 1px dashed #9ca3af;
    box-sizing: border-box;
  }
  .color-picker input {
    display: block;
    width: 1.5rem;
    height: 1.5rem;
    margin: -0.1875rem;
    border: 0;
    cursor: pointer;
  }
  .delete-btn {
    width: 100%;
    padding: 0.25rem;
    font-size: 0.6875rem;
    font-family: inherit;
    border: 1px solid #fecaca;
    border-radius: 0.25rem;
    background: #fef2f2;
    color: #b91c1c;
    cursor: pointer;
  }
  .delete-btn:hover {
    background: #fee2e2;
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
